import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

import API from '@/config/api';
// API imported from config;

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/staff/my-permissions`, getAuthHeaders());
      setPermissions(res.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Default to view-only for providers on error
      if (user.role === 'provider') {
        setPermissions({
          role: 'provider',
          has_full_access: false,
          permissions: {
            can_view_children: true,
            can_add_edit_children: false,
            can_manage_attendance: true,
            can_view_billing: false,
            can_create_invoices: false
          }
        });
      } else if (user.role === 'admin') {
        setPermissions({
          role: 'admin',
          has_full_access: true,
          permissions: {}
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Helper functions for checking permissions
  const hasPermission = useCallback((permissionKey) => {
    if (!permissions) return false;
    if (permissions.has_full_access) return true;
    return permissions.permissions?.[permissionKey] ?? false;
  }, [permissions]);

  const canEditChildren = useCallback(() => hasPermission('can_add_edit_children'), [hasPermission]);
  const canViewChildren = useCallback(() => hasPermission('can_view_children'), [hasPermission]);
  const canManageAttendance = useCallback(() => hasPermission('can_manage_attendance'), [hasPermission]);
  const canViewBilling = useCallback(() => hasPermission('can_view_billing'), [hasPermission]);
  const canCreateInvoices = useCallback(() => hasPermission('can_create_invoices'), [hasPermission]);
  const canManageWaitlist = useCallback(() => hasPermission('can_manage_waitlist'), [hasPermission]);
  const canEditCompliance = useCallback(() => hasPermission('can_edit_compliance'), [hasPermission]);
  const canViewStaffProfiles = useCallback(() => hasPermission('can_view_staff_profiles'), [hasPermission]);
  const canManageSchedules = useCallback(() => hasPermission('can_add_edit_schedules'), [hasPermission]);
  const canCreateReports = useCallback(() => hasPermission('can_create_reports'), [hasPermission]);
  const canMessageParents = useCallback(() => hasPermission('can_message_parents_and_staff'), [hasPermission]);

  const isAdmin = useCallback(() => permissions?.role === 'admin', [permissions]);
  const isParent = useCallback(() => permissions?.role === 'parent', [permissions]);
  const isStaff = useCallback(() => permissions?.role === 'provider', [permissions]);

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loading,
      refetchPermissions: fetchPermissions,
      hasPermission,
      canEditChildren,
      canViewChildren,
      canManageAttendance,
      canViewBilling,
      canCreateInvoices,
      canManageWaitlist,
      canEditCompliance,
      canViewStaffProfiles,
      canManageSchedules,
      canCreateReports,
      canMessageParents,
      isAdmin,
      isParent,
      isStaff
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;
